// src/utils/checkoutGrace.js

/**
 * Checkout grace period utilities
 * Allows customers already in checkout to complete their order
 * even if force closed override is activated
 *
 * Storage goes through safeStorage: these run while the checkout page renders,
 * and a browser that blocks sessionStorage must not crash it (#617).
 */
import { storageGet, storageSet, storageRemove } from './safeStorage';
import {
  FORCE_CLOSE_GRACE_MINUTES,
  isWithinForceCloseGrace,
} from './businessHours';

const CHECKOUT_ENTRY_KEY = 'checkoutEntryTime';
const GRACE_PERIOD_MINUTES = FORCE_CLOSE_GRACE_MINUTES;

/**
 * Record when user enters checkout
 * Only sets entry time if one doesn't already exist (prevents timer reset on refresh)
 */
export const recordCheckoutEntry = () => {
  const existingEntry = storageGet('sessionStorage', CHECKOUT_ENTRY_KEY);

  // If entry time already exists, don't overwrite it
  if (existingEntry) {
    return parseInt(existingEntry, 10);
  }

  // Set new entry time only if none exists
  const entryTime = Date.now();
  storageSet('sessionStorage', CHECKOUT_ENTRY_KEY, entryTime.toString());
  return entryTime;
};

/**
 * Get checkout entry time from storage
 * @returns {number|null} Entry timestamp or null if not found
 */
export const getCheckoutEntryTime = () => {
  const stored = storageGet('sessionStorage', CHECKOUT_ENTRY_KEY);
  return stored ? parseInt(stored, 10) : null;
};

/**
 * Clear checkout entry time from storage
 */
export const clearCheckoutEntry = () => {
  storageRemove('sessionStorage', CHECKOUT_ENTRY_KEY);
};

/**
 * Time left in the force-close grace window.
 * Counted from when the owner set the close, not from this visitor's page
 * load (#668).
 * @param {number|null} overrideSetAt - Epoch ms the force close was set
 * @returns {number} Remaining time in milliseconds
 */
export const getGracePeriodRemaining = (overrideSetAt) => {
  if (typeof overrideSetAt !== 'number') return 0;

  const gracePeriodMs = GRACE_PERIOD_MINUTES * 60 * 1000;
  const remaining = gracePeriodMs - (Date.now() - overrideSetAt);

  return Math.max(0, Math.min(gracePeriodMs, remaining));
};

/**
 * Check if user should be allowed to stay in checkout
 * despite force closed override being active.
 *
 * Only a customer who entered checkout *before* the close was set gets the
 * grace window, and only for the minutes right after it. Anyone arriving
 * later sees the closed message straight away (#668).
 *
 * @param {string|null} override - Business hours override state
 * @param {number|null} overrideSetAt - Epoch ms the force close was set
 * @returns {boolean} True if user can continue checkout
 */
export const canContinueCheckout = (override, overrideSetAt = null) => {
  // If not force closed, always allow
  if (override !== 'closed') {
    return true;
  }

  const entryTime = getCheckoutEntryTime();
  if (!entryTime || typeof overrideSetAt !== 'number') return false;

  return (
    entryTime <= overrideSetAt &&
    isWithinForceCloseGrace(overrideSetAt, Date.now(), GRACE_PERIOD_MINUTES)
  );
};
